import { useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from '@/components/ui/input-otp'
import { useAuthStore } from '@/features/auth/store/auth-store'
import { usePhone } from '../../hooks/use-phone'
import { PhoneVerifyInput } from '../shared/phone-verify-input'

const formSchema = z.object({
  otp: z.string().min(6, '请输入6位验证码').max(6, '请输入6位验证码'),
})

type OtpPhoneLoginFormProps = React.HTMLAttributes<HTMLDivElement>

export function OtpPhoneLoginForm({
  className,
  ...props
}: OtpPhoneLoginFormProps) {
  const { authLoginPhoneMutationAsync } = usePhone()
  const { handlePhoneLogin } = useAuthStore((s) => s.handlers)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isCodeSent, setIsCodeSent] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { otp: '' },
  })

  // eslint-disable-next-line react-hooks/incompatible-library
  const otp = form.watch('otp')

  async function onSubmit(data: z.infer<typeof formSchema>) {
    await handlePhoneLogin(
      { phone: phoneNumber, code: data.otp },
      authLoginPhoneMutationAsync
    )
  }

  return (
    <div className={cn('grid gap-4', className)} {...props}>
      {!isCodeSent ? (
        <PhoneVerifyInput
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          onCodeSent={() => setIsCodeSent(true)}
          type='login'
        />
      ) : (
        <div className='flex flex-col gap-4'>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='grid gap-2'>
              <FormField
                control={form.control}
                name='otp'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='sr-only'>One-Time Password</FormLabel>
                    <FormControl>
                      <InputOTP
                        maxLength={6}
                        onComplete={() => {
                          form.handleSubmit(onSubmit)()
                        }}
                        {...field}
                        containerClassName='justify-between sm:[&>[data-slot="input-otp-group"]>div]:w-12'
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className='mt-2 flex flex-col gap-2'>
                <Button
                  type='submit'
                  className='h-12 w-full text-base font-medium'
                  disabled={otp.length < 6 || form.formState.isSubmitting}
                >
                  登录
                </Button>
                <Button
                  variant='ghost'
                  size='sm'
                  className='text-muted-foreground hover:text-primary mx-auto text-xs font-normal'
                  onClick={() => setIsCodeSent(false)}
                  type='button'
                >
                  返回修改手机号
                </Button>
              </div>
            </form>
          </Form>
        </div>
      )}
    </div>
  )
}
